/**
 * Ownership-transfer review queue — the HTTP surface.
 *
 * Staging, matching and scoring all happen server-side in
 * `api/accounting/ownership-transfer-intake.ts`; this file only reads that queue and
 * carries a human's decision back. **Nothing here applies a transfer on its own** —
 * `apply` is reached from an explicit confirmation in the review modal, and a row's
 * `reviewScore` is a sort order and a bar width, never a branch.
 *
 * ── WHY THERE IS A MAPPER AND NOT JUST A CAST ──
 *
 * `fetchApi` returns `any`, so `as Promise<T>` ASSERTS a shape rather than checking one:
 * `{ data, pagination }` versus `{ items, total }` compiles clean either way and renders
 * an EMPTY list when wrong, which no API spec can see. Two of this route's fields are
 * exactly the kind that break silently under a cast:
 *
 *   - **timestamps arrive as ISO strings** (`isoOrUndefined` in api/accounting/gate.ts)
 *     while `effectiveOn` stays raw ms, because it is a business date. `Number(iso)` is
 *     `NaN`, so a component typed to expect ms would render every Received cell as "—".
 *   - **`reviewFlags` / `matchCandidateIds`** are the server's names; the UI reads
 *     `flags` and `matchCandidateIds`.
 *
 * `fromWire` is the single place either can be fixed, and the reason every component
 * below it can take `receivedAt: number` at face value.
 *
 * `association` on the packet is the MANAGEMENT COMPANY ("SBS Management"), never the
 * HOA — it arrives as `managementCompany` and is display-only provenance. The real
 * association comes from the matched ACCOUNT.
 */

import { apiService } from './api-service';

import type { PersonType } from '@/types/models';
import { parseDateToMs } from '@/utils/date-utils';
import type { PartyName } from '@/utils/parse-party-names';

/** Matches the `pathParts[1] === 'ownership-transfers'` branch in api/index.ts. */
const BASE = '/accounting/ownership-transfers';

const qs = (params: Record<string, unknown>): string => {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v == null || v === '') continue;
		sp.set(k, String(v));
	}
	const s = sp.toString();
	return s ? `?${s}` : '';
};

const post = (path: string, body: unknown) =>
	apiService.fetchApi(path, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify(body),
	});

/** What the MATCHER concluded. Mirrors the CHECK on `ownership_transfer_intake.resolved_status`. */
export type OwnershipTransferResolvedStatus =
	| 'matched'
	| 'matched_low_confidence'
	| 'ambiguous'
	| 'unmatched'
	| 'invalid';

/** Where the row sits in a person's queue — orthogonal to what the matcher thought. */
export type OwnershipTransferStatus = 'new' | 'applying' | 'applied' | 'dismissed';

export type OwnershipTransferSellerCheck = 'confirmed' | 'mismatch' | 'unknown';

/**
 * 'closing' = new construction: the seller is the builder, no prior account exists, and the
 * reviewer finishes the row with `createAccount` instead of `apply`.
 */
export type OwnershipTransferTransactionType = 'transfer' | 'closing';

/** Who decided the type. Absent = nobody said, so the 'transfer' default stands. */
export type OwnershipTransferTransactionTypeSource = 'agent' | 'builder' | 'reviewer';

/**
 * A pre-fill for a closing's create-account form, from the association shortcut-code
 * registry. Never authoritative — every field is editable and re-validated server-side.
 */
export interface OwnershipTransferNewAccountSuggestion {
	associationId: string;
	associationName: string | null;
	/** The registry's STREET code, e.g. "CF". Never derived. */
	shortcode: string;
	/** `<house number><shortcode>`, e.g. "5512CF". */
	accountNumber: string;
	/** "5512 Carr Farms Drive\nColumbus, OH 43230" — street line, newline, locality. */
	address: string;
	source: 'shortcode_registry';
}

/**
 * What the reviewer SAW when they pressed a finishing button — mirrors `ExpectedSchema` on the
 * route. The server refuses the request with 409 `REVIEW_STALE` when the row has moved since
 * (`updatedAt`), the action's account is no longer the one shown (`accountId`), or the address
 * preview the choice was made against has changed (`buyerAddress`). See the route for why.
 */
export interface ReviewExpectation {
	updatedAt: string;
	accountId: string | null;
	buyerAddress: OwnershipTransferBuyerAddress | null;
	/** The seller account's `updatedAt` (apply, transfer-from) — a seller edited since is stale too. */
	sellerUpdatedAt: string | null;
}

/** Mirrors `CreateAccountSchema` on the route. */
export interface CreateAccountFromClosingBody {
	associationId: string;
	accountNumber: string;
	address: string;
	/** Business date, UTC-midnight ms. Omit to default to the packet's effective date (else today ET). */
	openedOn?: number | null;
	/** At least one. The FIRST names the account ("<number> - <company || last || first>"). */
	owners: OwnershipTransferClosingOwnerInput[];
	/** Par-value ownership share, basis points of 100% (3.07% → 307). Par-value associations only. */
	parValueBps?: number | null;
	expected?: ReviewExpectation;
}

/**
 * A closing's owner — mirrors `ClosingOwnerSchema` on the route: a first name OR a company
 * (a builder often sells to an LLC or trust with no person named), the POST /people rule.
 * Its own type rather than a loosened {@link OwnershipTransferOwnerInput}, because the
 * TRANSFER path's schema still requires a first name and must keep saying so to the compiler.
 */
export type OwnershipTransferClosingOwnerInput = Omit<OwnershipTransferOwnerInput, 'firstName'> & {
	firstName?: string | null;
};

export type CreateAccountFromClosingResponse = OwnershipTransferQueueRow & { newAccountId: string };

export interface TransferBuilder {
	id: string;
	name: string;
	createdBy?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

/** The packet's source PDF, as the queue names it. */
export interface OwnershipTransferSourceDocument {
	/** The file's name, or "Source document" when only its Dropbox id is known. */
	fileName: string;
	/** 1-based page the packet cites — where the viewer opens. 1 when it cites none. */
	page: number;
	/** Dropbox `id:…` once known, for "Open in Dropbox". Null until the route first resolves it. */
	fileId: string | null;
}

/** An account the packet could be about: the matched one, plus any in contention. */
export interface OwnershipTransferAccountSummary {
	id: string;
	associationId: string;
	associationName?: string | null;
	address?: string | null;
	accountNumber?: string | null;
	lotNumber?: string | null;
	/** "4789LV - Steingass" — the seller-surname confirmation signal. */
	externalName?: string | null;
	status: string;
	archived: boolean;
	transferredToAccountId?: string | null;
	balanceCents: number;
	/** The account's version — a transfer's `ReviewExpectation.sellerUpdatedAt`. */
	updatedAt?: string | null;
	owners: Array<{ firstName: string | null; lastName: string | null; email: string | null }>;
}

/**
 * An account that already exists for the packet's lot (#632) — usually the builder's placeholder
 * ("4827ES - Epcon-Carr Farms"). Found by the registry's account number for the lot and/or by the
 * street address; the number matters because a placeholder can carry the builder's HQ address.
 */
export interface OwnershipTransferLotAccount extends OwnershipTransferAccountSummary {
	matchedBy: Array<'account_number' | 'address'>;
	/** The builder's placeholder for the lot. Wording only — it gates nothing. */
	builderPlaceholder: boolean;
	/** Live and never transferred: only such an account can be the seller of a transfer. */
	transferable: boolean;
	/** Where a transfer from this account would file the buyers. Absent on a 409's `existingAccount`. */
	buyerAddress?: OwnershipTransferBuyerAddress;
}

/**
 * The buyer account's address a transfer will use (`resolveBuyerAddress` on the server): the
 * seller's, unless the packet names a different street — then the packet's (`fromPacket`).
 */
export interface OwnershipTransferBuyerAddress {
	address: string | null;
	fromPacket: boolean;
}

/** Mirrors `TransferFromSchema` on the route: the apply body plus the seller. */
export interface TransferFromAccountBody extends ApplyOwnershipTransferBody {
	sellerAccountId: string;
}

/** A charge the server would accept in `moveChargeIds` — its list, not ours, is authority. */
export interface OwnershipTransferMovableCharge {
	id: string;
	postedOn: number;
	dueOn?: number;
	amountCents: number;
	category?: string | null;
	memo?: string | null;
}

/** Mirrors `OwnerInputSchema` on the route / `TransferOwnerInput` in the repo. */
export interface OwnershipTransferOwnerInput {
	firstName: string;
	lastName?: string | null;
	email?: string | null;
	phone?: string | null;
	companyName?: string | null;
	/** An entity buyer (trust, LLC) — stored typed a company. Absent ⇒ the server derives it. */
	personType?: PersonType | null;
}

export interface OwnershipTransferQueueRow {
	id: string;
	source: string;
	sourceRef?: string | null;
	/** A real moment (stage time), normalized to ms — render it viewer-local. */
	receivedAt: number;
	/** "T001" — PER-BATCH sequential, so a reference label only. Never an identity key. */
	transferId?: string | null;

	propertyAddress: string;
	sellerName?: string | null;
	buyerName?: string | null;
	/** `parsePartyNames` output, pre-filling the review form's buyer rows. */
	buyerParties: PartyName[];
	/** Business date: UTC-midnight ms. Render with `formatBusinessDate`, never locale. */
	effectiveOn?: number | null;
	/**
	 * Recorded for the packet's audit trail ONLY. v1 posts nothing against it — no
	 * charge, no journal entry — which is why the column renders with a qualifier
	 * rather than as a figure someone could total.
	 */
	transferFeeCents?: number | null;
	/** The packet's own `association` field: the management company. Display-only. */
	managementCompany?: string | null;
	extractionConfidence?: number | null;
	notes?: string | null;
	sourcePages?: unknown;

	resolvedStatus: OwnershipTransferResolvedStatus;
	matchedAccountId?: string | null;
	matchedAssociationId?: string | null;
	matchSource?: string | null;
	/**
	 * Ids only — the list route does not expand them. The review modal reads the
	 * expanded `candidateAccounts` off the DETAIL route instead.
	 */
	matchCandidateIds: string[];
	sellerCheck?: OwnershipTransferSellerCheck | null;
	sellerCheckDetail?: string | null;
	/** 0..1. Ordering and a bar width. Nothing may branch on it. */
	reviewScore?: number | null;
	/**
	 * Raw flag slugs, from the server's `reviewFlags`. Typed loosely on purpose: a flag
	 * the server adds tomorrow (`stale_match` was added by the apply route after the
	 * matcher's union was written) must render as a chip rather than disappear.
	 */
	flags: string[];

	status: OwnershipTransferStatus;
	appliedAt?: number | null;
	createdAccountId?: string | null;
	dismissedAt?: number | null;
	dismissReason?: string | null;
	errorMessage?: string | null;
	/** The row's version as of this read (ISO) — sent back as `ReviewExpectation.updatedAt`. */
	updatedAt?: string | null;
	/** Set only when a reviewer marked the packet already handled, against this account. */
	handledAccountId?: string | null;
	handledNote?: string | null;

	/** Always present on the wire ('transfer' for rows that predate the column). */
	transactionType: OwnershipTransferTransactionType;
	transactionTypeSource?: OwnershipTransferTransactionTypeSource | null;

	/** The PDF the packet was extracted from, or null when none is linked. Bytes: `sourcePath(id)`. */
	sourceDocument?: OwnershipTransferSourceDocument | null;

	/**
	 * OPTIONAL enrichment the list route may add later (a LEFT JOIN onto accounts +
	 * associations). Present ⇒ the queue can label the matched account and its
	 * association without a second request; absent ⇒ the page falls back to the
	 * association registry it already has cached. Never `managementCompany`.
	 */
	matchedAccountNumber?: string | null;
	matchedAccountExternalName?: string | null;
	matchedAssociationName?: string | null;
}

/** The row plus everything the review modal needs to decide: packet, accounts, charges. */
export interface OwnershipTransferDetail extends OwnershipTransferQueueRow {
	rawPayload: Record<string, unknown> | null;
	/** The matched account and every candidate, with owners — the tie-breaker fields. */
	candidateAccounts: OwnershipTransferAccountSummary[];
	/** Empty until the row has a match: there is no account to move charges off. */
	movableCharges: OwnershipTransferMovableCharge[];
	/** Set only when exactly ONE registry entry claims the street. Null ⇒ the human picks. */
	newAccountSuggestion: OwnershipTransferNewAccountSuggestion | null;
	/** Every registry entry claiming the street; several ⇒ offer them, pick none. */
	newAccountCandidates: OwnershipTransferNewAccountSuggestion[];
	/** Accounts that already exist for this lot. Empty for a matched transfer or a decided row. */
	existingLotAccounts: OwnershipTransferLotAccount[];
	/** Where Apply will file the buyer, for the matched seller. Null until the packet has a match. */
	buyerAddress: OwnershipTransferBuyerAddress | null;
}

export interface OwnershipTransferListParams {
	status?: OwnershipTransferStatus;
	resolvedStatus?: OwnershipTransferResolvedStatus;
	transactionType?: OwnershipTransferTransactionType;
	q?: string;
	page?: number;
	limit?: number;
}

export interface OwnershipTransferListResponse {
	data: OwnershipTransferQueueRow[];
	pagination?: { page: number; limit: number; total: number; pages: number };
}

export interface ApplyOwnershipTransferBody {
	/** Business date, UTC-midnight ms. */
	transferDate: number;
	newOwner: OwnershipTransferOwnerInput;
	/** Co-buyers on the SAME new account — packets routinely name two people. */
	additionalOwners?: OwnershipTransferOwnerInput[];
	/**
	 * Fully-unpaid charges to carry over. **Empty by default**: whether this month's
	 * dues follow the property is a term of the sale the packet does not state, so it
	 * is ticked by a person rather than defaulted by us.
	 */
	moveChargeIds: string[];
	/**
	 * The buyer address the reviewer confirmed. Sent only when the packet's street and the seller's
	 * differ; absent ⇒ the server keeps the seller's (same street) or takes the packet's.
	 */
	address?: string | null;
	expected?: ReviewExpectation;
}

/** The applied row, plus the account the buyer ended up on. */
export type ApplyOwnershipTransferResponse = OwnershipTransferQueueRow & { newAccountId: string };

/**
 * One queue row as the Worker sends it. Deliberately loose where the wire is loose —
 * this type exists to be MAPPED, not to be rendered.
 */
interface WireRow {
	[key: string]: unknown;
	id: string;
	propertyAddress: string;
	receivedAt?: string | number | null;
	appliedAt?: string | number | null;
	dismissedAt?: string | number | null;
	buyerParties?: unknown[];
	reviewFlags?: string[];
	matchCandidateIds?: string[];
	resolvedStatus: OwnershipTransferResolvedStatus;
	status: OwnershipTransferStatus;
}

/** Shape-checked, because a cast here would hand the viewer `page: undefined` and open nowhere. */
const sourceDocumentFromWire = (raw: unknown): OwnershipTransferSourceDocument | null => {
	if (!raw || typeof raw !== 'object') return null;
	const doc = raw as Record<string, unknown>;
	const page = Number(doc.page);
	return {
		fileName: typeof doc.fileName === 'string' && doc.fileName ? doc.fileName : 'Source document',
		page: Number.isInteger(page) && page > 0 ? page : 1,
		fileId: typeof doc.fileId === 'string' && doc.fileId ? doc.fileId : null,
	};
};

const fromWire = (raw: WireRow): OwnershipTransferQueueRow => {
	const { reviewFlags, ...rest } = raw;
	return {
		...(rest as unknown as OwnershipTransferQueueRow),
		// ISO on the wire, ms in the UI. `parseDateToMs` takes either, so a Worker that
		// later switches back to raw ms does not break this.
		receivedAt: parseDateToMs(raw.receivedAt) ?? 0,
		appliedAt: parseDateToMs(raw.appliedAt),
		dismissedAt: parseDateToMs(raw.dismissedAt),
		buyerParties: (raw.buyerParties ?? []) as PartyName[],
		matchCandidateIds: raw.matchCandidateIds ?? [],
		flags: reviewFlags ?? [],
		transactionType: raw.transactionType === 'closing' ? 'closing' : 'transfer',
		sourceDocument: sourceDocumentFromWire(raw.sourceDocument),
	};
};

export const ownershipTransferService = {
	/**
	 * The queue. `limit` defaults high because the page filters its chips in memory —
	 * a network round-trip per chip blanks the table, which CLAUDE.md's stable-inputs
	 * rule exists to prevent.
	 */
	list: async (params: OwnershipTransferListParams = {}): Promise<OwnershipTransferListResponse> => {
		const res = await apiService.fetchApi(`${BASE}${qs({ limit: 500, ...params })}`) as {
			data?: WireRow[];
			pagination?: { page: number; limit: number; total: number; pages: number };
		};
		return { data: (res?.data ?? []).map(fromWire), pagination: res?.pagination };
	},

	get: async (id: string): Promise<OwnershipTransferDetail> => {
		const res = await apiService.fetchApi(`${BASE}/${id}`) as WireRow & {
			rawPayload?: Record<string, unknown> | null;
			candidateAccounts?: OwnershipTransferAccountSummary[];
			movableCharges?: OwnershipTransferMovableCharge[];
			newAccountSuggestion?: OwnershipTransferNewAccountSuggestion | null;
			newAccountCandidates?: OwnershipTransferNewAccountSuggestion[];
			existingLotAccounts?: OwnershipTransferLotAccount[];
			buyerAddress?: OwnershipTransferBuyerAddress | null;
		};
		return {
			...fromWire(res),
			rawPayload: res?.rawPayload ?? null,
			candidateAccounts: res?.candidateAccounts ?? [],
			movableCharges: res?.movableCharges ?? [],
			newAccountSuggestion: res?.newAccountSuggestion ?? null,
			newAccountCandidates: res?.newAccountCandidates ?? [],
			existingLotAccounts: res?.existingLotAccounts ?? [],
			buyerAddress: res?.buyerAddress ?? null,
		};
	},

	/**
	 * The badge's number, without dragging 500 rows behind it.
	 *
	 * `pending` counts `new` AND `applying`; `needsChecking` is the `applying` subset — an
	 * apply that claimed a row and never came back. fetchApi's return is an ASSERTED cast,
	 * so this shape must match the route's JSON exactly: a mismatch compiles clean and
	 * reads `undefined` at runtime.
	 */
	/**
	 * Path of the packet's source PDF, for the AUTHENTICATED blob fetch in
	 * `utils/document-download.ts` — never a URL handed to the browser, which cannot send our
	 * auth header.
	 */
	sourcePath: (id: string): string => `${BASE}/${encodeURIComponent(id)}/source`,

	pendingCount: () =>
		apiService.fetchApi(`${BASE}/count`) as Promise<{ pending: number; needsChecking: number }>,

	/**
	 * Point an ambiguous or unmatched row at an account WITHOUT applying anything.
	 *
	 * A separate call because the apply route refuses a row with no match at all — and
	 * because someone can settle the routing question now and leave the transfer itself
	 * to whoever handles the sale.
	 */
	resolve: async (id: string, accountId: string): Promise<OwnershipTransferQueueRow> =>
		fromWire(await post(`${BASE}/${id}/resolve`, { accountId }) as WireRow),

	apply: async (id: string, body: ApplyOwnershipTransferBody): Promise<ApplyOwnershipTransferResponse> => {
		const res = await post(`${BASE}/${id}/apply`, body) as WireRow & { newAccountId: string };
		return { ...fromWire(res), newAccountId: res.newAccountId };
	},

	dismiss: async (id: string, reason: string): Promise<OwnershipTransferQueueRow> =>
		fromWire(await post(`${BASE}/${id}/dismiss`, { reason }) as WireRow),

	/**
	 * Finish a CLOSING: create the new account plus its owners, and mark the row applied.
	 * 409 `NOT_A_CLOSING` on a transfer row, `ACCOUNT_NUMBER_TAKEN` when the number is in use
	 * (the row goes back to `new` with the reason in `errorMessage`).
	 */
	createAccount: async (id: string, body: CreateAccountFromClosingBody): Promise<CreateAccountFromClosingResponse> => {
		const res = await post(`${BASE}/${id}/create-account`, body) as WireRow & { newAccountId: string };
		return { ...fromWire(res), newAccountId: res.newAccountId };
	},

	/**
	 * Finish a CLOSING on a lot that already has an account (#632) as the TRANSFER it is: the
	 * existing account (the builder's placeholder) is the seller, closed as `<num>-CLSD-<date>`,
	 * and the buyers take the clean number at the packet's address.
	 */
	transferFrom: async (id: string, body: TransferFromAccountBody): Promise<ApplyOwnershipTransferResponse> => {
		const res = await post(`${BASE}/${id}/transfer-from`, body) as WireRow & { newAccountId: string };
		return { ...fromWire(res), newAccountId: res.newAccountId };
	},

	/** Settle a packet somebody already did by hand, against the account they did it on. Changes no account. */
	markHandled: async (
		id: string,
		accountId: string,
		note?: string,
		expected?: ReviewExpectation,
	): Promise<OwnershipTransferQueueRow> =>
		fromWire(await post(`${BASE}/${id}/mark-handled`, { accountId, note: note || undefined, expected }) as WireRow),

	/** A reviewer's Transfer ↔ Closing switch. Only a `new` row; re-scores it server-side. */
	setTransactionType: async (
		id: string,
		transactionType: OwnershipTransferTransactionType,
	): Promise<OwnershipTransferQueueRow> =>
		fromWire(await post(`${BASE}/${id}/transaction-type`, { transactionType }) as WireRow),

	listBuilders: async (): Promise<TransferBuilder[]> => {
		const res = await apiService.fetchApi(`${BASE}/builders`) as { data?: TransferBuilder[] };
		return res?.data ?? [];
	},

	createBuilder: (name: string) => post(`${BASE}/builders`, { name }) as Promise<TransferBuilder>,

	deleteBuilder: (id: string) =>
		apiService.fetchApi(`${BASE}/builders/${id}`, { method: 'DELETE' }) as Promise<{ id: string; deleted: boolean }>,

	/**
	 * The way OUT of `applying` — record what a reviewer found when they went and looked.
	 *
	 * `applying` means "we started and do not know that we finished", and it used to be a
	 * state with no exit: every other mutation refused such a row, so the queue could show
	 * the problem and never let anyone answer it.
	 *
	 * The server does NOT take either answer on trust. `applied` must name the account the
	 * transfer created and that account must point back at this packet's seller; `not-applied`
	 * is refused outright if the seller is already closed and linked, because reopening a
	 * transfer that did complete is how a property gets transferred twice.
	 */
	settle: async (
		id: string,
		outcome: 'applied' | 'not-applied',
		note: string,
		createdAccountId?: string,
	): Promise<OwnershipTransferQueueRow> =>
		fromWire(await post(`${BASE}/${id}/settle`, { outcome, note, createdAccountId }) as WireRow),

	/**
	 * Re-run matching over rows that never resolved to one account — the usual cause is
	 * a packet that landed before the lot existed as an account.
	 *
	 * Chunked and re-drivable: the caller loops on `done`, exactly like every other bulk
	 * path in this app, so no single request runs long enough to be killed.
	 *
	 * `skipped` counts rows a CONCURRENT sweep — a click that overlapped the cron — already
	 * covered between this chunk's read and its write. It is the difference between
	 * "re-checked nothing" and "someone else re-checked it for you", and without it a
	 * perfectly healthy race reports zero work done.
	 */
	rescan: (
		chunkLimit = 100,
		cursor: { matchedAfter?: string | null; matchedDone?: boolean; buildersVersion?: number | null } = {},
	) =>
		post(`${BASE}/rescan`, { chunkLimit, ...cursor }) as Promise<{
			processed: number; skipped: number; remaining: number; done: boolean;
			/**
			 * Cursor for the matched-row reclassification (builder-list changes reaching rows that
			 * already matched an account). Send all three back on the next call, unchanged; the
			 * server discards the cursor when `buildersVersion` shows the builder list moved.
			 */
			matchedAfter: string | null;
			matchedDone: boolean;
			buildersVersion: number;
		}>,
};
